// authApi.js (FIXED)
import axios from "axios";
import { jwtDecode } from "jwt-decode";

// Create base API configurations
const baseConfig = {
    // baseURL: "https://localhost:5000/api",
    baseURL: "https://api.ankiverse.com/api",
    withCredentials: true,
};

// Create different API instances for different services
const createApiService = (path = "") => {
    const service = axios.create({
        ...baseConfig,
        baseURL: `${baseConfig.baseURL}${path}`,
    });

    // Attach request interceptor to add auth token
    service.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem("accessToken");
            if (token) {
                config.headers["Authorization"] = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Attach response interceptor to handle token refresh
    service.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // ***** START OF FIX *****
            // The URL for the login request within the 'auth' service is '/login'
            // We check this to prevent the interceptor from trying to refresh the token on a failed login attempt.
            const isLoginAttempt = originalRequest.url.includes("/login");

            // Only attempt to refresh the token if the error is 401, it's not a retry,
            // AND it's not the login attempt itself that failed.
            if (error.response && error.response.status === 401 && !originalRequest._retry && !isLoginAttempt) {
            // ***** END OF FIX *****

                originalRequest._retry = true;
                try {
                    const { data } = await axios.post(
                        `${baseConfig.baseURL}/auth/refresh-token`,
                        {},
                        { withCredentials: true }
                    );
                    localStorage.setItem("accessToken", data.accessToken);
                    
                    // Important: Update the header for the request that is being retried.
                    originalRequest.headers["Authorization"] = `Bearer ${data.accessToken}`;
                    
                    // Reschedule proactive refresh after the token update
                    scheduleRefresh();
                    
                    // Retry the original request with the new token
                    return service(originalRequest);
                } catch (refreshError) {
                    console.error("Token refresh failed. Logging out.", refreshError);
                    localStorage.removeItem("accessToken");

                    // Forcing a hard redirect to /login. This will clear all React state,
                    // which is a common and acceptable pattern for a total auth failure.
                    window.location.href = "/login";
                    
                    // It's good practice to not let the original error propagate after a redirect.
                    return Promise.resolve();
                }
            }
            
            // For all other errors, or for the failed login attempt, reject the promise.
            // This allows the component's .catch() block to handle it.
            return Promise.reject(error);
        }
    );

    return service;
};

// Helper: Decode token and return its expiration time (in ms)
function getTokenExpiry(token) {
    try {
        const decoded = jwtDecode(token);
        // JWT exp is in seconds; convert to ms
        return decoded.exp * 1000;
    } catch (error) {
        console.error("Failed to decode token:", error);
        return null;
    }
}

let refreshTimeout;

// Schedule a proactive token refresh before expiry
function scheduleRefresh() {
    // Clear any existing timeout to prevent multiple refresh schedules
    if (refreshTimeout) clearTimeout(refreshTimeout);
    
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    
    const expiryTime = getTokenExpiry(token);
    if (!expiryTime) return;

    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;
    // Refresh 1 minute (60000ms) before expiry, or immediately if it's already close.
    const refreshDelay = Math.max(timeUntilExpiry - 60000, 0);

    console.log(`Token will be refreshed in ${Math.round(refreshDelay / 1000)}s`);
    refreshTimeout = setTimeout(refreshAccessToken, refreshDelay);
}

// Function to refresh the access token
async function refreshAccessToken() {
    console.log("Attempting proactive token refresh...");
    try {
        const { data } = await axios.post(
            `${baseConfig.baseURL}/auth/refresh-token`,
            {},
            { withCredentials: true }
        );
        localStorage.setItem("accessToken", data.accessToken);
        console.log("Proactive token refresh successful.");
        // Reschedule the proactive refresh with the new token
        scheduleRefresh();
    } catch (err) {
        console.error("Proactive token refresh failed. Logging out.", err);
        localStorage.removeItem("accessToken");
        window.location.href = "/login";
    }
}

// If an access token exists on app load, schedule its refresh
if (typeof window !== "undefined" && localStorage.getItem("accessToken")) {
    scheduleRefresh();
}

// Create API instances for different services
const authApi = {
    auth: createApiService("/auth"),
    email: createApiService("/email"),
    user: createApiService("/user"),
    // You can add more services here as your application grows
    baseApi: createApiService("")
};

export default authApi;